import React, { useState, useEffect, forwardRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { StatusBadge } from "../ui/StatusBadge";
import { Invoice } from "../../types/invoice.types";
import { getDashboardSummary } from "../../service/dashboard/DashboardServices";
import { Toast } from "primereact/toast";
import moment from "moment";
import { IDashboardProps } from "@/types/dashboard.types";
import DatePicker from "react-datepicker";
import { PermissionVariables } from "../../shared/constant/PermissionVariables";
import {
  ReportSummaryDto,
  MonthlyRevenueDto,
  InvoiceStatusDto,
  TopClientDto,
  TopItemDto,
} from "../../types/report.types";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getDashboardData } from "../../service/report/ReportService";

type PeriodOption = {
  label: string;
  value: "year" | "month" | "quarter";
};

export const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("month");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: moment().startOf("month").format("DD/MM/YYYY"),
    endDate: moment().endOf("month").format("DD/MM/YYYY"),
  });

  // State for report data
  const [summary, setSummary] = useState<ReportSummaryDto | null>(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenueDto[]>([]);
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatusDto[]>([]);
  const userPermissions = useSelector(
    (state: any) => state.permission.permissions
  );
  const localization = useSelector((state: any) => state.localization?.localization);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = React.useRef<Toast>(null);
  const periodOptions: PeriodOption[] = [
    { label: localization?.Month || "Month", value: "month" },
    // { label: "Quarter", value: "quarter" },
    { label: localization?.Year || "Year", value: "year" },
  ];

  useEffect(() => {
    updateDateRange(selectedDate);
  }, [selectedPeriod, selectedDate]);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const calculateChange = (
    current: number,
    previous?: number
  ): { value: string; isPositive: boolean } => {
    if (!previous) return { value: "N/A", isPositive: true };

    const change = ((current - previous) / previous) * 100;
    return {
      value: `${Math.abs(change).toFixed(1)}%`,
      isPositive: change >= 0,
    };
  };

  const updateDateRange = (date: Date) => {
    let startDate: moment.Moment;
    let endDate: moment.Moment;
    const momentDate = moment(date);

    switch (selectedPeriod) {
      case "month":
        startDate = momentDate.clone().startOf("month");
        endDate = momentDate.clone().endOf("month");
        break;
      case "quarter":
        startDate = momentDate.clone().startOf("quarter");
        endDate = momentDate.clone().endOf("quarter");
        break;
      case "year":
        startDate = momentDate.clone().startOf("year");
        endDate = momentDate.clone().endOf("year");
        break;
      default:
        startDate = momentDate.clone().startOf("year");
        endDate = momentDate.clone().endOf("year");
    }

    setDateRange({
      startDate: startDate.format("DD/MM/YYYY"),
      endDate: endDate.format("DD/MM/YYYY"),
    });
  };

  const fetchDashboardData = async () => {
    try {
      const props: IDashboardProps = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      const response = await getDashboardSummary(dispatch, props);

      if (response) {
        setDashboardData(response);
        setSummary(response.
          reportSummary
        );
        setMonthlyRevenue(response.monthlyRevenue);
        setInvoiceStatus(
          response?.invoiceStatus?.map((status: any) => ({
            ...status,
            color:
              status.name === "Paid"
                ? "#06923E"
                : status.name === "Pending"
                  ? "#FEBA17"
                  : status.name === "Partial"
                    ? "#C562AF"
                    : status.name === "Write_Off"
                    ? "#0ABAB5"
                    : "#A31D1D",
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.current?.show({
        severity: "error",
        summary: localization?.Error || "Error",
        detail: localization?.FailedToLoadDashboardData || "Failed to load dashboard data",
      });
    }
  };

  // Template for the invoice number column
  const invoiceNumberTemplate = (rowData: Invoice) => {
    return (
      <a
        href={`/invoice/${rowData.invoice_id}`}
        className="text-gold hover:underline font-medium"
        onClick={(e) => {
          e.preventDefault();
          navigate(`/invoice/${rowData.invoice_id}`);
        }}
      >
        {rowData.invoice_number}
      </a>
    );
  };

  // Template for the status column
  const statusTemplate = (rowData: Invoice) => {
    return <StatusBadge status={rowData?.status} />;
  };

  // Template for the amount column
  const amountTemplate = (rowData: any) => {
    return <span className="font-medium">${rowData?.amount?.toFixed(2)}</span>;
  };

  // Template for the date column
  const dateTemplate = (rowData: Invoice) => {
    return moment(rowData?.issue_date).format("MMM Do YY");
  };

  // Handle period change
  const handlePeriodChange = (option: string) => {
    setSelectedPeriod(option);
    // Reset the selectedDate to ensure proper view initialization
    setSelectedDate(new Date());
  };

  const CustomInput = forwardRef(({ value, onClick }: any, ref: any) => (
    <div className="example-custom-input" onClick={onClick} ref={ref}>
      {value}
    </div>
  ));

  // Render appropriate calendar based on period type
  const renderDateSelector = () => {
    switch (selectedPeriod) {
      case "month":
        return (
          <Calendar
            key={`month-calendar-${selectedPeriod}`}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.value as Date)}
            className="text-sm"
            view="month"
            dateFormat="mm/yy"
            showIcon
          />
        );
      // case "quarter":
      //   // PrimeReact doesn't have a quarter view, so we'll use month view
      //   // and then calculate the quarter in the updateDateRange function
      //   return (
      //     <DatePicker
      //     key={`quarter-picker-${selectedPeriod}`}
      //     selected={selectedDate}
      //     customInput={<CustomInput />}
      //     onChange={(date: Date | null) => date && setSelectedDate(date)}
      //     dateFormat="MM/yy"
      //     showMonthYearPicker
      //   />
      //   );
      case "year":
        return (
          <Calendar
            key={`year-calendar-${selectedPeriod}`}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.value as Date)}
            view="year"
            dateFormat="yy"
            className="text-sm"
            showIcon
          />
        );
      default:
        return null;
    }
  };

  // Get the period display name
  const getPeriodDisplay = () => {
    const date = moment(selectedDate);
    switch (selectedPeriod) {
      case "month":
        return date.format("MMMM YYYY");
      case "quarter":
        return `Q${Math.ceil(date.month() / 3)} ${date.format("YYYY")}`;
      case "year":
        return date.format("YYYY");
      default:
        return "";
    }
  };

  return (
    <div className="">
      <Toast ref={toast} />
      <h1 className="text-xl font-bold surface-900 mb-4">{localization?.Dashboard || "Dashboard"}</h1>
      <div className="space-y-4 p-0 rounded-md">
        <div className="flex flex-wrap gap-4 justify-between items-center bg-white dark:bg-gray-600 px-4 py-3 rounded-md shadow-sm border-l-4 border-gold">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center">
                <span className="text-sm dark:text-light-gray text-gray-800 mr-2">{localization?.Period || "Period:"}</span>
                <Dropdown
                  value={selectedPeriod}
                  options={periodOptions}
                  onChange={(e) => handlePeriodChange(e.value)}
                  className="text-sm h-[38px] border border-light-gray focus:border-[var(--primaryColor)] rounded-md"
                  placeholder={localization?.SelectPeriod || "Select Period"}
                  optionLabel="label"
                  panelClassName="border border-gold shadow-md"
                />
              </div>
              <div className="flex items-center">
                <span className="text-sm dark:text-light-gray text-gray-800 mr-2">{localization?.Date || "Date:"}</span>
                <span className="p-0">{renderDateSelector()}</span>
              </div>
            </div>
          </div>
          {userPermissions?.includes(PermissionVariables.createInvoice) && (
            <button
              onClick={() => navigate("/invoice/create")}
              className="p-button h-[38px] w-[135px] bg-gold hover:bg-gold hover:opacity-80 text-white shadow-sm px-4 rounded-md transition-all duration-300 transform hover:scale-[0.98] border-none"
              style={{ backgroundColor: 'var(--gold)' }}
            >
              {localization?.CreateInvoice || "Create Invoice"}
            </button>
          )}
        </div>
        {/* Period Display */}
        <div className="bg-white px-4 py-2 rounded-md shadow-sm border-l-4 border-gold">
          <div className="text-sm text-medium-gray">
            <span className="font-medium">{localization?.CurrentPeriod || "Current Period:"}</span>{" "}
            {getPeriodDisplay()}
            <span className="ml-2 text-xs text-medium-gray">
              ({dateRange.startDate} - {dateRange.endDate})
            </span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Card className="bg-gradient-to-br from-cream to-white border-l-4 border-gold shadow-sm p-2 card_content_padding">
            <h3 className="text-lg font-bold mb-3 text-charcoal">{localization?.Outstanding || "Outstanding"}</h3>
            <p className="text-xl font-bold mt-4 text-gold">
              {summary ? formatCurrency(summary.outstandingInvoices) : "$0.00"}
            </p>
          </Card>

          <Card className="bg-gradient-to-br from-cream to-white border-l-4 border-alert-red shadow-sm p-2 card_content_padding">
            <h3 className="text-lg font-bold mb-3 text-charcoal">{localization?.Overdue || "Overdue"}</h3>
            <p className="text-xl font-bold mt-4 text-alert-red">
              {dashboardData?.overdueInvoices?.count || 0}{" "}
              {dashboardData?.overdueInvoices?.count === 1
                ? (localization?.Invoice || "invoice")
                : (localization?.Invoices || "invoices")}
            </p>
          </Card>

          <Card className="bg-gradient-to-br from-cream to-white border-l-4 border-success-green shadow-sm p-2 card_content_padding">
            <h3 className="text-lg font-bold mb-3">{localization?.PaidInPeriod || "Paid in Period"}</h3>
            <p className="text-xl font-bold mt-4 text-success-green">
              {dashboardData?.paidThisPeriod?.count || 0}{" "}
              {dashboardData?.paidThisPeriod?.count === 1
                ? (localization?.Invoice || "invoice")
                : (localization?.Invoices || "invoices")}
            </p>
          </Card>
          
          <Card className="bg-gradient-to-br from-cream to-white border-l-4 border-success-green shadow-sm p-2 card_content_padding">
            <h3 className="text-lg font-bold mb-3">{localization?.TotalRevenue || "Total Revenue"}</h3>
            <p className="text-xl font-bold mt-4 text-success-green">
              {summary ? formatCurrency(summary.totalRevenue) : "$0.00"}
            </p>
          </Card>
          <Card className="bg-gradient-to-br from-cream to-white border-l-4 border-success-green shadow-sm p-2 card_content_padding">
            <h3 className="text-lg font-bold mb-3">{localization?.AverageInvoiceValue || "Average Invoice Value"}</h3>
            <p className="text-xl font-bold mt-4 text-gold">
              {summary ? formatCurrency(summary.averageInvoiceValue) : "$0.00"}
            </p>
          </Card>
          <Card className="bg-gradient-to-br from-cream to-white border-l-4 border-success-green shadow-sm p-2 card_content_padding">
            <h3 className="text-lg font-bold mb-3">{localization?.TotalInvoices || "Total Invoices"}</h3>
            <p className="text-xl font-bold mt-4 text-blue-800">
              {summary ? formatCurrency(summary.totalInvoices) : "$0.00"}
            </p>
          </Card>
        </div>


        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Revenue Chart */}
          <Card className="card_content_padding">
            <div className="p-0">
              <h2 className="text-gray-800 text-lg font-semibold mb-4 mt-1">
                Monthly Revenue
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyRevenue}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => formatCurrency(Number(value))}
                    />
                    <Bar dataKey="value" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>

          {/* Invoice Status Chart */}
          <Card className="card_content_padding">
            <div className="p-0">
              <h2 className="text-gray-800 text-lg font-semibold mb-4 mt-1">
                Invoice Status
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={invoiceStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} (${(percent * 100).toFixed(0)}%)`
                      }
                    >
                      {invoiceStatus?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Legend />
                    <Tooltip formatter={(value) => `${value}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Invoices */}
        <Card
          className="shadow-sm border-t-0 border-r-4 overflow-hidden border-b-0 border-l-4 border-gold p-0 card_custonized"
          contentClassName="p-0"
        >
          {/* <div className="bg-gradient-to-r from-cream to-transparent px-3 py-2 border-b border-light-gray"></div> */}
          <div className="bg-medium-gray p-4">
            <h3 className="dark:text-base text-white font-semibold mt-0 mb-0 p-0">
              {localization?.RecentInvoices || "Recent Invoices"}
            </h3>
          </div>
          <div className="p-4">
            <DataTable
              value={dashboardData?.recentInvoices || []}
              emptyMessage={localization?.NoInvoicesFound || "No invoices found"}
              responsiveLayout="scroll"
              stripedRows
              className="p-datatable-sm"
              rowHover
              size="small"
            >
              <Column
                field="invoiceNumber"
                header={localization?.Invoice || "Invoice"}
                body={invoiceNumberTemplate}
                sortable
                headerClassName="bg-light-gray text-charcoal text-xs py-2"
                bodyClassName="py-2 text-sm"
              />
              <Column
                field="client_name"
                header={localization?.Client || "Client"}
                sortable
                headerClassName="bg-light-gray text-charcoal text-xs py-2"
                bodyClassName="py-2 text-sm"
              />
              <Column
                field="date"
                header={localization?.Date || "Date"}
                body={dateTemplate}
                sortable
                headerClassName="bg-light-gray text-charcoal text-xs py-2"
                bodyClassName="py-2 text-sm"
              />
              <Column
                field="status"
                header={localization?.Status || "Status"}
                body={statusTemplate}
                sortable
                headerClassName="bg-light-gray text-charcoal text-xs py-2"
                bodyClassName="py-2 text-sm"
              />
              <Column
                field="total"
                header={localization?.Amount || "Amount"}
                body={amountTemplate}
                sortable
                style={{ textAlign: "left" }}
                headerClassName="bg-light-gray text-charcoal text-xs py-2"
                bodyClassName="py-2 text-sm"
              />
            </DataTable>
          </div>


          <div className="px-3 py-2 border bg-gray-100 flex justify-end">
            <button
              onClick={() => navigate("/invoices")}
              className="view_invoice bg-gray-50 border text-sm py-2 px-4 rounded-md transition-all duration-300"

            >
              {localization?.ViewAllInvoices || "View All Invoices →"}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};